import { RootState } from "app/store";
import CarRequestCard from "components/card/CarRequestCard";
import Menu from "components/common/Menu";
import CarRequestHeader from "components/header/CarRequestHeader";
import { SideBarTabs } from "constants/tabs";
import useCars from "hook/useCars";
import { useTabActive } from "hook/useTabActive";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CommonStyles } from "styles/CommonStyles";
import PageHeader from "components/common/PageHeader";
import EmptyState from "components/common/EmptyState";
import LoadMoreButton from "components/common/LoadMoreButton";

function CarRequestPage() {
  const location = useLocation();
  const selectedRequestId = String(location.state?.requestId || "");
  const { getCarRequests } = useCars();
  const { carRequests } = useSelector((state: RootState) => state.request);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const loadData = async () => {
    await getCarRequests({ setLoading }, {});
  };

  useTabActive(SideBarTabs.CarRequest.name);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedRequestId || !carRequests.length) return;
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`car-request-${selectedRequestId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [carRequests, selectedRequestId]);

  const handleLoadMore = async () => {
    await getCarRequests(
      { setLoading: setLoadingMore, offset: carRequests.length },
      { more: true }
    );
  };

  return (
    <div className={`${CommonStyles.pageWrapper}`}>
      <Menu />
      <PageHeader
        title="Car requests"
        description="Follow up with buyers who requested help finding a car."
      />
      <div className={ClassName.container}>
        <section className={ClassName.callSection}>
          {loading ? (
            <div className="w-full items-center justify-center py-6 flex">
              <div className="primaryLoader"></div>
            </div>
          ) : (
            <div className="flex w-full flex-col">
              <CarRequestHeader />
              <div className={ClassName.callList}>
                {carRequests.map((request, index) => {
                  const requestId = String(request._id || index);
                  return (
                    <div
                      id={`car-request-${requestId}`}
                      key={requestId}
                      className={
                        selectedRequestId === requestId
                          ? ClassName.selectedTarget
                          : ClassName.target
                      }
                    >
                      <CarRequestCard request={request} />
                    </div>
                  );
                })}
                {!carRequests.length ? (
                  <EmptyState
                    title="No car requests"
                    description="New buyer requests will appear here."
                  />
                ) : null}
                {carRequests && carRequests.length > 19 && (
                  <LoadMoreButton
                    onClick={handleLoadMore}
                    loading={loadingMore}
                  />
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default CarRequestPage;

const ClassName = {
  container: "flex flex-1 flex-col text-primaryText",
  callSection:
    "flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4",
  callList: "flex max-h-[calc(100vh-205px)] min-h-56 flex-col overflow-y-auto",
  target: "rounded-xl transition-colors",
  selectedTarget: "rounded-xl bg-red-50 ring-2 ring-primary/40 transition-colors",
};
